/**
 * Shared helpers for nest / compile flows: dependency edges crossing a task set.
 * Mirrors logic in FlowchartView/hooks/useNestDrop.js (unlockConditions + dependsOn).
 */

import { getAllConditionTaskIds } from './ConditionsDropdown/utils/conditionHelpers';

export function getEffectiveDependencyIds(task, allTasks = null) {
  const ids = getAllConditionTaskIds(task.unlockConditions, allTasks);
  if (ids.length > 0) return ids;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.map((dep) => (typeof dep === 'string' ? dep : dep?.taskId)).filter(Boolean);
}

/**
 * Upstream / downstream counts relative to a set of task ids (e.g. dragged or selected nodes).
 * Upstream: dependencies from outside the set into the set.
 * Downstream: dependencies from inside the set to tasks outside the set.
 */
export function getNestDependencyInfo(tasks, draggedIds) {
  const draggedSet = draggedIds instanceof Set ? draggedIds : new Set(draggedIds);
  const info = {
    upstreamCount: 0,
    downstreamCount: 0,
    upstreamIds: new Set(),
    downstreamIds: new Set(),
  };

  for (const draggedId of draggedSet) {
    const draggedTask = tasks.find((t) => t.id === draggedId);
    if (!draggedTask) continue;

    const depIds = getEffectiveDependencyIds(draggedTask, tasks);
    for (const depId of depIds) {
      if (depId && !draggedSet.has(depId)) {
        info.upstreamIds.add(depId);
      }
    }

    for (const task of tasks) {
      if (draggedSet.has(task.id)) continue;
      const taskDepIds = getEffectiveDependencyIds(task, tasks);
      for (const depId of taskDepIds) {
        if (depId === draggedId) {
          info.downstreamIds.add(task.id);
        }
      }
    }
  }

  info.upstreamCount = info.upstreamIds.size;
  info.downstreamCount = info.downstreamIds.size;

  return info;
}

/**
 * Forward adjacency: depId -> Set of task ids that depend on depId (flow edges depId → task).
 */
function buildForwardAdjacency(tasks) {
  const adj = new Map();
  for (const t of tasks) {
    const tid = t.id;
    for (const depId of getEffectiveDependencyIds(t, tasks)) {
      if (!depId) continue;
      if (!adj.has(depId)) adj.set(depId, new Set());
      adj.get(depId).add(tid);
    }
  }
  return adj;
}

function reachableForward(fromId, toId, adj) {
  if (fromId === toId) return true;
  const queue = [fromId];
  const seen = new Set([fromId]);
  while (queue.length) {
    const x = queue.shift();
    for (const y of adj.get(x) || []) {
      if (y === toId) return true;
      if (!seen.has(y)) {
        seen.add(y);
        queue.push(y);
      }
    }
  }
  return false;
}

/** BFS following only edges whose head is in S (both endpoints must be in S for traversal step). */
function reachableForwardWithinSet(fromId, toId, S, adj) {
  if (fromId === toId) return true;
  const queue = [fromId];
  const seen = new Set([fromId]);
  while (queue.length) {
    const x = queue.shift();
    for (const y of adj.get(x) || []) {
      if (!S.has(y)) continue;
      if (y === toId) return true;
      if (!seen.has(y)) {
        seen.add(y);
        queue.push(y);
      }
    }
  }
  return false;
}

/**
 * Convex: no "hole" — any directed path between two selected nodes cannot be forced through V \ S.
 */
function isConvexDependencySegment(tasks, S, adj) {
  const ids = Array.from(S);
  for (const u of ids) {
    for (const v of ids) {
      if (u === v) continue;
      if (!reachableForward(u, v, adj)) continue;
      if (!reachableForwardWithinSet(u, v, S, adj)) return false;
    }
  }
  return true;
}

/** Undirected connectivity using only edges whose two endpoints lie in S. */
function isWeaklyConnectedSelection(tasks, S) {
  const ids = Array.from(S);
  const und = new Map();
  const addUnd = (a, b) => {
    if (!und.has(a)) und.set(a, new Set());
    if (!und.has(b)) und.set(b, new Set());
    und.get(a).add(b);
    und.get(b).add(a);
  };
  for (const t of tasks) {
    const tid = t.id;
    if (!S.has(tid)) continue;
    for (const depId of getEffectiveDependencyIds(t, tasks)) {
      if (S.has(depId)) addUnd(depId, tid);
    }
  }

  const start = ids[0];
  const conn = new Set([start]);
  const q = [start];
  while (q.length) {
    const x = q.shift();
    for (const y of und.get(x) || []) {
      if (!conn.has(y)) {
        conn.add(y);
        q.push(y);
      }
    }
  }
  return conn.size === ids.length;
}

/**
 * External nodes that feed into S (unique ids outside S referenced as dependencies of tasks in S).
 */
function getExternalForkJoinBoundaryIds(tasks, S) {
  const Sset = S instanceof Set ? S : new Set(S);
  const upstreamExternal = new Set();
  const downstreamExternal = new Set();

  for (const t of tasks) {
    if (!Sset.has(t.id)) continue;
    for (const depId of getEffectiveDependencyIds(t, tasks)) {
      if (!Sset.has(depId)) upstreamExternal.add(depId);
    }
  }
  for (const t of tasks) {
    if (Sset.has(t.id)) continue;
    for (const depId of getEffectiveDependencyIds(t, tasks)) {
      if (Sset.has(depId)) downstreamExternal.add(t.id);
    }
  }
  return { upstreamExternal, downstreamExternal };
}

/**
 * True when the selection can be collapsed to one wrapper without breaking workflow semantics:
 * - **Convex**: no path between two selected nodes is forced through a node outside S.
 * - And either:
 *   - **Single trail**: weakly connected inside S, or
 *   - **Parallel fork–join**: one external upstream (all selected nodes ultimately fed from the same
 *     outside predecessor) and one external downstream (all exits from S lead to the same outside
 *     successor), so multiple disjoint chains in S become parallel streams inside the nest.
 */
export function isCompressibleTrailSegment(tasks, selectedIds) {
  const S = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const ids = Array.from(S);
  if (ids.length < 2) return false;

  const adj = buildForwardAdjacency(tasks);

  if (!isConvexDependencySegment(tasks, S, adj)) return false;

  if (isWeaklyConnectedSelection(tasks, S)) return true;

  const { upstreamExternal, downstreamExternal } = getExternalForkJoinBoundaryIds(tasks, S);
  if (upstreamExternal.size === 1 && downstreamExternal.size === 1) return true;

  return false;
}

/**
 * True when selected nodes can be compiled into a nested campaign: either fully isolated in the
 * dependency graph, or forming a compressible trail (same workflow segment, rewire to wrapper).
 */
export function canCompileMissionsIntoNestedCampaign(tasks, selectedTaskIds) {
  if (!Array.isArray(selectedTaskIds) || selectedTaskIds.length < 2) return false;
  const draggedSet = new Set(selectedTaskIds);
  const selectedTasks = tasks.filter((t) => draggedSet.has(t.id));
  if (selectedTasks.length !== selectedTaskIds.length) return false;

  const depInfo = getNestDependencyInfo(tasks, draggedSet);
  if (depInfo.upstreamCount === 0 && depInfo.downstreamCount === 0) return true;
  return isCompressibleTrailSegment(tasks, draggedSet);
}
